import React, { useState, useEffect } from 'react'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { useHistory, useParams } from 'react-router-dom'
import { FiSearch } from 'react-icons/fi'
import { FaRegTrashAlt } from 'react-icons/fa'
import axios from 'axios'
import { BASE_URL } from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import Spinner from '../../components/Spinner'

const groupTypes = [
  { value: "farmers", label: "Farmers" },
  { value: "processors", label: "Processors" },
]

const EditGroup = (props) => {
  const history = useHistory()
  const routeState = props.location.state?.state
  // Try to get groupId from route state, else from URL params
  const params = useParams()
  const groupId = routeState?.id || params.id
  // State
  const [groupName, setGroupName] = useState("")
  const [description, setDescription] = useState("")
  const [groupType, setGroupType] = useState("")
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [searchLoading, setSearchLoading] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [selectedMembers, setSelectedMembers] = useState([])
  const [errors, setErrors] = useState({})

  // Fetch group details on mount
  useEffect(() => {
    if (!groupId) {
      return
    }
    setLoading(true)
    const token = localStorage.getItem("token")
    axios.get(`${BASE_URL}groups/${groupId}/`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(res => {
        const g = res.data
        setGroupName(g.group_name || "")
        setDescription(g.description || "")
        setGroupType(g.group_type || "")
        setSelectedMembers((g.members || []).map(u => ({
          value: u.id,
          label: u.name || u.email || `User ${u.id}`
        })))
      })
      .catch((err) => {
        ErrorNotification("Failed to fetch group details")
      })
      .finally(() => setLoading(false))
  }, [groupId])

  // Async search for members (unchanged)
  useEffect(() => {
    if (!searchText) {
      setSearchResults([])
      return
    }
    setSearchLoading(true)
    const token = localStorage.getItem("token")
    let url = groupType === "processors"
      ? `${BASE_URL}aggregator/getallmillers/?search_item=${searchText}`
      : `${BASE_URL}aggregator/getallaggregators/?search_item=${searchText}`
    axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(res => {
        let users = []
        if (groupType === "processors") {
          users = res.data.users.map(u => ({
            value: u.miller,
            label: `${u.user.firstname} ${u.user.lastname}`
          }))
        } else {
          users = res.data.users.map(u => ({
            value: u.user.id,
            label: `${u.user.firstname} ${u.user.lastname}`
          }))
        }
        setSearchResults(users)
      })
      .catch(() => setSearchResults([]))
      .finally(() => setSearchLoading(false))
  }, [searchText, groupType])

  // Add member (unchanged)
  const addMember = (member) => {
    if (!selectedMembers.some(m => m.value === member.value)) {
      setSelectedMembers([...selectedMembers, member])
    }
    setSearchText("")
    setSearchResults([])
  }

  // Remove member (unchanged)
  const removeMember = (value) => {
    setSelectedMembers(selectedMembers.filter(m => m.value !== value))
  }

  // Clear all (unchanged)
  const clearAll = () => setSelectedMembers([])

  // Validation
  const validate = () => {
    const errs = {}
    if (!groupName.trim()) errs.groupName = "Group name is required"
    if (!description.trim()) errs.description = "Group description is required"
    if (!groupType) errs.groupType = "Group type is required"
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  // Submit handler (PATCH group details)
  const handleSave = (e) => {
    e.preventDefault()
    if (!validate()) return
    setLoading(true)
    const token = localStorage.getItem("token")
    axios.patch(`${BASE_URL}groups/${groupId}/`, {
      group_name: groupName,
      group_type: groupType,
      description: description,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        SuccessNotification("Group updated successfully!")
        history.goBack()
      })
      .catch(() => ErrorNotification("Failed to update group"))
      .finally(() => setLoading(false))
  }

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10 space-y-6">
      {loading && (
        <div className="w-full h-96 flex items-center justify-center">
          <Spinner />
        </div>
      )}
      {!loading && (
        <>
          <div className="w-full">
            <button onClick={() => history.goBack()} className="flex flex-row items-center">
              <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
              <p className="text-sm font-light capitalize dark:text-white">
                Back
              </p>
            </button>

            <h2 className="text-2xl font-bold dark:text-white">
              Edit Group Details
            </h2>
          </div>

          <form className="bg-white mt-2 rounded-lg dark:bg-dark" onSubmit={handleSave}>
            <div className="px-3 py-4 md:px-8 md:py-8">
              {/* Group Info */}
              <div className="mb-6">
                <h3 className="text-yellowcolor text-lg font-semibold mb-4">Group Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Group Name<span className="text-red-500">*</span></label>
                    <input
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      placeholder="Enter group name"
                      value={groupName}
                      onChange={e => setGroupName(e.target.value)}
                      disabled={loading}
                    />
                    {errors.groupName && <div className="text-xs text-red-500 mt-1">{errors.groupName}</div>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1 dark:text-white">Group Type<span className="text-red-500">*</span></label>
                    <select
                      className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                      value={groupType}
                      onChange={e => setGroupType(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">eg. Farmers</option>
                      {groupTypes.map(gt => (
                        <option key={gt.value} value={gt.value}>{gt.label}</option>
                      ))}
                    </select>
                    {errors.groupType && <div className="text-xs text-red-500 mt-1">{errors.groupType}</div>}
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-sm font-medium mb-1 dark:text-white">Group Description<span className="text-red-500">*</span></label>
                  <input
                    className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 pr-10 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                    placeholder="Created this group for easy access to send requests"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    disabled={loading}
                  />
                  {errors.description && <div className="text-xs text-red-500 mt-1">{errors.description}</div>}
                </div>
              </div>
              {/* <hr className="my-6" /> */}
              {/* Group Members (display only, not editable here) */}
              {/* <div className="mb-6">
                <h3 className="text-yellowcolor text-lg font-semibold mb-4">Group Members (Participants)</h3>
                {selectedMembers.length > 0 ? (
                  <div className="mt-4 flex flex-wrap gap-2 items-center">
                    {selectedMembers.map(member => (
                      <span key={member.value} className="flex items-center bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-full px-3 py-1 text-sm font-light">
                        {member.label}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400 text-sm">No members in this group.</div>
                )}
              </div> */}
            </div>

            <div className='border-t p-4 z-50 border-gray-200 flex flex-row items-end justify-end w-full bg-white dark:bg-dark flex-shrink-0 dark:border-border-dark'>
              <div className="flex flex-row items-end justify-end gap-4">
                <button
                  type="button"
                  onClick={() => history.goBack()}
                  className='px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg'
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className={`px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

          </form>
        </>
      )}

    </div>
  )
}

export default EditGroup